/*! glb-variant-cart

  Script: inietta nei form di Add-to-cart gli input hidden properties[N] con colori/titoli selezionati
  dalla UI globale window.__GLB_COLOR_UI__.

  Funzioni esposte:
  - install(optsIn): configura opzioni e installa listener submit/click (container o document).
  - injectColorsIntoForm(form): inietta input hidden properties[N] con i valori correnti dei colori.
  - addSelectedToCart(params): trova o usa un form, assicura input variante id e inietta le proprietà.
*/

(function globalInit(rootName = '__GLB_VARIANT_CART__') {
  if (typeof window === 'undefined' || window[rootName]) return;

  const DEFAULT = {
    selectorAddToCartBtn: '[data-add-to-cart], button.add-to-cart, form[action*="/cart/add"] button[type="submit"]',
    containerSelector: null,
    autoInstall: true,
    injectDebounceMs: 40
  };

  let opts = Object.assign({}, DEFAULT);
  let cachedRoot = null;
  const lastInject = new WeakMap();

  // Converte testo multilinea in oggetto numerato properties[1], properties[2], ...
  function buildProperties(text) {
    if (!text) return {};
    const lines = Array.isArray(text) ? text.slice() : String(text).split(/\r\n|\n/);
    const out = {};
    let idx = 1;
    for (let ln of lines) {
      ln = String(ln || '').trim();
      if (!ln) continue;
      const m = ln.match(/^[^:]+:\s*(.+)$/);
      out[String(idx++)] = m ? m[1].trim() : ln;
    }
    return out;
  }

  // Legge la selezione colori dalla UI globale e restituisce stringa multilinea "Title: Color\n..."
  function getSelectedColorsText() {
    const G = window.__GLB_COLOR_UI__;
    if (!G || typeof G !== 'object') return '';
    const ui = G.instance;
    if (!ui || typeof ui !== 'object') return '';

    const entries = [];

    if (typeof ui.snapshot === 'function') {
      try {
        const s = ui.snapshot();
        if (s && Array.isArray(s.selected)) {
          for (let i = 0; i < s.selected.length; i++) {
            const it = s.selected[i] || {};
            const title = it.title ?? it.sublevelName ?? it.sublevel ?? `Insert ${i + 1}`;
            const color = it.colorName ?? it.color ?? '';
            entries.push({ title: String(title).trim(), color: String(color).trim() });
          }
        }
      } catch (e) {
        // fallback a seconda fonte
      }
    }

    if (!entries.length && ui.SELECTED && typeof ui.SELECTED.entries === 'function') {
      try {
        for (const [, v] of ui.SELECTED.entries()) {
          const title = v?.title ?? v?.sublevelName ?? v?.sublevel ?? `Insert ${entries.length + 1}`;
          const color = v?.colorName ?? v?.color ?? '';
          entries.push({ title: String(title).trim(), color: String(color).trim() });
        }
      } catch (e) {
        // ignore
      }
    }

    if (!entries.length) return '';
    return entries.map(e => (e.color ? `${e.title}: ${e.color}` : `${e.title}`)).join('\n');
  }

  // Inietta input hidden properties[N] nel form; salta l'iniezione se testo colori invariato
  function injectColorsIntoForm(form) {
    if (!form || form.tagName !== 'FORM') return { ok: false, reason: 'invalid_form' };

    const last = lastInject.get(form) || 0;
    const now = Date.now();
    if (now - last < (opts.injectDebounceMs || 40)) return { ok: false, reason: 'debounced' };
    lastInject.set(form, now);

    const sc = getSelectedColorsText();
    if (!sc) return { ok: false, reason: 'no_colors' };

    // skip se identico all'ultima iniezione per quel form
    try {
      if (form.getAttribute('data-glb-last-colors') === sc) return { ok: true, injected: 0, reason: 'unchanged' };
    } catch (e) {}

    // rimuove solo input generati in precedenza
    const prev = form.querySelectorAll('input.__glb_vc_prop');
    for (let i = 0; i < prev.length; i++) prev[i].remove();

    const props = buildProperties(sc);
    if (!Object.keys(props).length) {
      form.removeAttribute && form.removeAttribute('data-glb-last-colors');
      return { ok: false, reason: 'no_props' };
    }

    const frag = document.createDocumentFragment();
    let count = 0;
    for (const k in props) {
      if (!Object.prototype.hasOwnProperty.call(props, k)) continue;
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = `properties[${k}]`;
      input.value = String(props[k] ?? '');
      input.className = '__glb_vc_prop';
      input.setAttribute('data-glb-generated', '1');
      frag.appendChild(input);
      count++;
    }
    if (count) form.appendChild(frag);

    try { form.setAttribute('data-glb-last-colors', sc); } catch (e) {}
    form.setAttribute && form.setAttribute('data-glb-colors-injected', '1');

    return { ok: true, injected: count, props };
  }

  // Trova pulsante/form add-to-cart partendo da cachedRoot o document
  function findAddToCartControls() {
    const root = cachedRoot || (opts.containerSelector ? (document.querySelector(opts.containerSelector) || document) : document);
    cachedRoot = root;

    let btn = null;
    try {
      btn = root.querySelector(opts.selectorAddToCartBtn) || (root !== document ? document.querySelector(opts.selectorAddToCartBtn) : null);
    } catch (e) { btn = null; }

    let form = null;
    if (btn && typeof btn.closest === 'function') form = btn.closest('form');

    if (!form) {
      try {
        form = root.querySelector('form[action*="/cart/add"]') || document.querySelector('form[action*="/cart/add"]') || root.querySelector('form') || document.querySelector('form');
      } catch (e) { form = null; }
    }

    return { btn, form };
  }

  // Assicura input hidden name="id" (variant) e lo imposta se variantId fornito
  function ensureVariantInput(form, variantId) {
    if (!form) return null;
    let input = form.querySelector('input[name="id"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'id';
      form.appendChild(input);
    }
    if (variantId !== undefined && variantId !== null) input.value = String(variantId);
    return input;
  }

  // Trova o usa form esplicito, assicura variant id e inietta proprietà
  function addSelectedToCartDelegated({ variantId = null, form: explicitForm = null, formSelector = null } = {}) {
    let targetForm = null;

    if (explicitForm != null) {
      if (!(explicitForm instanceof Element)) return { ok: false, reason: 'invalid_form' };
      if (String(explicitForm.tagName).toUpperCase() !== 'FORM') return { ok: false, reason: 'invalid_form' };
      targetForm = explicitForm;
    }

    if (!targetForm && typeof formSelector === 'string' && formSelector.trim()) {
      const f = document.querySelector(formSelector);
      if (f && f.tagName === 'FORM') targetForm = f;
      else return { ok: false, reason: 'invalid_form_selector' };
    }

    if (!targetForm) {
      const controls = findAddToCartControls();
      targetForm = controls.form;
    }

    if (!targetForm) return { ok: false, reason: 'no_form_found' };

    ensureVariantInput(targetForm, variantId);
    const injectRes = injectColorsIntoForm(targetForm);
    if (!injectRes || !injectRes.ok) return { ok: false, reason: injectRes && injectRes.reason ? injectRes.reason : 'inject_failed', error: injectRes && injectRes.error ? injectRes.error : undefined };

    let formId = targetForm.getAttribute('id') || targetForm.getAttribute('name') || null;
    if (!formId) {
      if (!targetForm.hasAttribute('data-glb-form-key')) {
        targetForm.setAttribute('data-glb-form-key', 'glbform-' + Math.random().toString(36).slice(2, 9));
      }
      formId = targetForm.getAttribute('data-glb-form-key');
    }

    try {
      document.dispatchEvent(new CustomEvent('glb:colors-injected', { detail: { variantId, injected: injectRes.injected, formId }, bubbles: true }));
    } catch (e) {}

    return { ok: true, method: 'inject-only', injected: injectRes.injected, props: injectRes.props, formId };
  }

  // Installa listener submit e click sul container specificato o su document
  function attachFormSubmitInjection() {
    cachedRoot = opts.containerSelector ? (document.querySelector(opts.containerSelector) || document) : document;

    cachedRoot.addEventListener('submit', ev => {
      const form = ev.target;
      if (!form || form.tagName !== 'FORM') return;
      const action = (form.getAttribute('action') || '').toLowerCase();
      const isCart = action.includes('/cart/add') || action.includes('/cart');
      let hasBtn = false;
      try { hasBtn = !!form.querySelector(opts.selectorAddToCartBtn); } catch (e) { hasBtn = false; }
      if (isCart || hasBtn) injectColorsIntoForm(form);
    }, true);

    cachedRoot.addEventListener('click', ev => {
      const tgt = ev.target;
      if (!tgt) return;
      const btn = typeof tgt.closest === 'function' ? tgt.closest(opts.selectorAddToCartBtn) : null;
      if (!btn) return;
      const form = btn.closest && btn.closest('form');
      if (form) injectColorsIntoForm(form);
    }, true);
  }

  const API = {
    install(optsIn = {}) { opts = Object.assign({}, DEFAULT, optsIn || {}); cachedRoot = null; attachFormSubmitInjection(); },
    injectColorsIntoForm(form) { try { return injectColorsIntoForm(form); } catch (e) { return { ok: false, error: String(e) }; } },
    addSelectedToCart(params = {}) { try { return addSelectedToCartDelegated(params); } catch (e) { return { ok: false, error: String(e) }; } }
  };

  window[rootName] = window[rootName] || {};
  Object.assign(window[rootName], API);

  if (opts.autoInstall) setTimeout(() => { API.install(); }, 150);
})();
